import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle, faWalking } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 550px;
  width: 90%;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideIn 0.3s ease-out forwards;

  @keyframes slideIn {
    from {
      transform: scale(0.8) translateY(-20px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: scale(1.1);
  }
`;

const Icon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Message = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1.6;
  margin-bottom: 1.5rem;

  strong {
    color: ${props => props.theme.colors.accent};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const WalkButton = styled.a`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.875rem 1.75rem;
  }
`;

const NewYearsDayWalkPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const [showWalkInvitation, setShowWalkInvitation] = useState(true);

  useEffect(() => {
    // Check if popup has already been shown in this session
    const popupShown = sessionStorage.getItem('newYearsDayWalkPopupDec31');
    if (popupShown) {
      setHasBeenShown(true);
      return;
    }

    // Show popup on December 31st, 2025 and January 1st, 2026
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const hours = today.getHours();
    
    const isDec31st = year === 2025 &&
                      month === 11 && // December is month 11 (0-based)
                      day === 31;

    const isJan1st = year === 2026 &&
                     month === 0 && // January is month 0 (0-based)
                     day === 1;

    // Hide walk invitation on January 1st after 3PM
    const isJan1stAfter3PM = isJan1st && hours >= 15; // 3PM = 15:00

    if ((isDec31st || isJan1st) && !hasBeenShown) {
      setIsVisible(true);
      setShowWalkInvitation(!isJan1stAfter3PM);
    }
  }, [hasBeenShown]);

  const handleClose = () => {
    setIsVisible(false);
    setHasBeenShown(true);
    // Remember that popup was shown for this session
    sessionStorage.setItem('newYearsDayWalkPopupDec31', 'true');
  };

  if (!isVisible || hasBeenShown) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <Icon>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Icon>
        <Title>Castle Closed</Title>
        <Message>
          Belvoir Castle is closed until the 14th March.
          <br /><br />
          <strong>The Gardens and Retail Village remain open daily.</strong>
          {showWalkInvitation && (
            <>
              <br /><br />
              Please join us on a New Year's Day walk in support of Dove Cottage.
            </>
          )}
        </Message>
        {showWalkInvitation && (
          <WalkButton 
            href="https://www.belvoircastle.com/event/cG9zdDozNTQ="
            onClick={(e) => {
              e.stopPropagation();
              // Track that user clicked the walk button
              sessionStorage.setItem('newYearsDayWalkPopupDec31', 'true');
            }}
          >
            <FontAwesomeIcon icon={faWalking} />
            Join the Walk
          </WalkButton>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default NewYearsDayWalkPopup;

